import React from 'react';
import clsx from 'clsx';
import camelCase from 'lodash/camelCase';
import produce from 'immer';

import {useTranslation} from 'common/i18n/hooks';
import {SvgIconReact} from 'common/components/svg-icon';
import {prompt} from 'common/components/confirm';
import {MetricEntryBehavior} from 'common/query-builder/query-entry-section/types';
import {MetricType} from 'common/types/reports/bookmark';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useMetricEntries} from 'common/report/insights/contexts/metrics';
import {SectionItem} from 'common/components/items-menu/types';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {BehaviorTypeIcons} from 'common/report/insights/util/metrics';
import {Placement} from 'common/components/tooltip/types';
import {TooltipReact} from 'common/components/tooltip';

import {OverflowControls} from '../common/controls';
import {checkUpsellForMetricBehavior} from '../utils';
// oxlint-disable-next-line no-cycle
import {BehaviorSelector} from '../common/behavior-selector';
import styles from './index.module.styl';
import type {RowProps} from './catalog';

type MetricBehaviorProps = RowProps & {
  metricType: MetricType;
  children?: React.ReactNode;
  behavior?: MetricEntryBehavior;
};

/**
 * Defines the wrapping component for a behavior definition.
 * This component is responsible for:
 * - Saving a behavior
 * - Reset changes on a changed behavior
 * - Selecting a saved behavior
 */
export function MetricBehaviorBase(props: MetricBehaviorProps) {
  const {t} = useTranslation();
  const behaviors = useBehaviors();

  const currentProject = useCurrentProject()?.get()?.value;
  const checkUpsell = checkUpsellForMetricBehavior(currentProject);
  const {saveBehavior, resetBehavior, getSaveBehaviorDisabledMessage} = useMetricEntries();

  const [isSaving, setIsSaving] = React.useState(false);
  const behavior = props.entry.behavior;
  const hasUnsavedChanges = behavior.id ? behavior.hasUnsavedChanges : false;

  const updateBehavior = (updates: Partial<MetricEntryBehavior>) => {
    props.updateEntry(
      produce(props.entry, (draft) => {
        Object.assign(draft.behavior, updates);
      }),
    );
  };

  const updateDisabledMessage = getSaveBehaviorDisabledMessage({
    index: props.index,
    parentMetricIndex: props.parentMetricIndex,
  });

  const handleSaveBehavior = async (saveAsNew = false) => {
    if (checkUpsell.shouldUpsell) {
      return checkUpsell.upsellAction();
    }

    setIsSaving(true);
    await saveBehavior({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      saveAsNew,
    });
    setIsSaving(false);
  };

  const handleResetBehavior = () => {
    resetBehavior({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
    });
  };

  const handleRenameBehavior: () => void = async () => {
    const promptResponse = await prompt(
      {
        /** @todo(metrics) plug in automatic naming here */
        'initial-input-value': behavior.name || t(`query-builder.metrics.new-definition`),
        'allow-empty-input-value': false,
        'show-input': true,
      },
      {
        inputValidator(stagedInput) {
          const trimmed = stagedInput.trim();
          const labelIsUnchanged = trimmed === behavior.name;
          const isUnique = behaviors
            .getAllValues()
            .filter((value) => value.id !== behavior.id)
            .every((value) => value.name !== trimmed);
          return labelIsUnchanged || isUnique ? null : t(`components.create-entity.errors.name-exists`);
        },
      },
      {
        title: ({t}) => t(`query-builder.actions.rename`),
        confirmButtonLabel: ({t}) => t(`common.done`),
      },
    );

    if (promptResponse.didConfirm) {
      updateBehavior({name: promptResponse.inputValue.trim()});
    }
  };

  return (
    <div className={clsx(styles.behavior, styles[camelCase(`is-${props.metricType}-metric`)])}>
      {/* metric header area */}
      <div className={styles.header}>
        {/* behavior label/selector */}
        <div
          className={clsx(styles.rowIcon, {
            [styles.hasUnsavedChanges]: hasUnsavedChanges,
          })}
        >
          {hasUnsavedChanges ? (
            <TooltipReact placement={Placement.Top} text={t(`query-builder.behaviors.unsaved-changes-tooltip`)} />
          ) : null}
          <SvgIconReact icon={BehaviorTypeIcons[props.metricType as keyof typeof BehaviorTypeIcons]} />
        </div>

        <div className={styles.content}>
          <BehaviorSelector {...props} />
        </div>

        {/* indicate sync status  */}
        {isSaving && <span className={styles.status}>{t(`common.saving_suspense`)}</span>}

        {/* metric controls */}
        <div className={clsx(styles.actions, {[styles.isOpen]: props.activeMenu?.menu === `behavior-overflow`})}>
          {/* metric overflow controls for saving, resetting, etc... */}
          <OverflowControls
            onMenuChange={(isOpen) => props.setActiveMenu?.(isOpen ? {menu: `behavior-overflow`} : null)}
            items={[
              {
                items: [
                  // show "Update Behavior" only when saved and with local changes
                  hasUnsavedChanges && {
                    label: t(`query-builder.actions.update`),
                    icon: `push-up`,
                    onSelect: () => handleSaveBehavior(),
                    isDisabled: Boolean(updateDisabledMessage),
                    tooltip: updateDisabledMessage ? {text: updateDisabledMessage} : null,
                  },

                  // show "Save Behavior" only when unsaved
                  !behavior.id && {
                    label: t(`query-builder.actions.save`),
                    icon: `save`,
                    onSelect: () => handleSaveBehavior(),
                    ...(checkUpsell.shouldUpsell && {icon: `brand-upgrade`}),
                  },

                  // show "Save as New Behavior" only when rendering a saved behavior
                  behavior.id && {
                    label: t(`query-builder.behaviors.save-as-new`),
                    icon: `save`,
                    onSelect: () => handleSaveBehavior(true),
                    ...(checkUpsell.shouldUpsell && {
                      icon: `brand-upgrade`,
                    }),
                  },

                  // show "Reset" only when dealing with a saved behavior with local changes
                  hasUnsavedChanges && {
                    label: t(`query-builder.actions.reset`),
                    icon: `undo`,
                    onSelect: handleResetBehavior,
                  },

                  {
                    label: t(`query-builder.actions.rename`),
                    icon: `edit`,
                    onSelect: handleRenameBehavior,
                  },
                ].filter(Boolean) as SectionItem[],
              },
            ]}
          />
        </div>
      </div>

      {/* behavior definition */}
      <div className={styles.content}>{props.children}</div>
    </div>
  );
}
